"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _componentsBase = require("@telus-uds/components-base");
var _styledComponents = _interopRequireDefault(require("styled-components"));
var _OrderedListBase = _interopRequireDefault(require("../OrderedList/OrderedListBase"));
var _ItemBase = _interopRequireDefault(require("../OrderedList/ItemBase"));
var _Image = _interopRequireDefault(require("../Image"));
var _utils = require("../utils");
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const [selectProps, selectedSystemPropTypes] = (0, _componentsBase.selectSystemProps)([_utils.htmlAttrs]);
const row = (rowSize, rowBorderWidth) => ({
  flexBasis: `calc(100% / ${rowSize})`,
  [`:not(:nth-of-type(${rowSize}n + 1))`]: {
    borderLeftWidth: `${rowBorderWidth}px`
  },
  [`:nth-of-type(n+${rowSize + 1})`]: {
    borderTopWidth: `${rowBorderWidth}px`
  }
});
const Container = /*#__PURE__*/(0, _styledComponents.default)(_OrderedListBase.default).withConfig({
  displayName: "WaffleGrid__Container",
  componentId: "components-web__sc-ec8g0a-0"
})({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'flex-start'
});
const Item = /*#__PURE__*/(0, _styledComponents.default)(_ItemBase.default).withConfig({
  displayName: "WaffleGrid__Item",
  componentId: "components-web__sc-ec8g0a-1"
})(_ref => {
  let {
    rowSize,
    itemPadding,
    itemBorderColor,
    rowBorderWidth
  } = _ref;
  return {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexGrow: 1,
    flexShrink: 1,
    padding: `${itemPadding}px`,
    boxSizing: 'border-box',
    borderStyle: 'solid',
    borderColor: itemBorderColor,
    borderWidth: 0,
    '& > a': {
      alignSelf: 'center'
    },
    ...row(rowSize, rowBorderWidth)
  };
});
const Center = /*#__PURE__*/(0, _styledComponents.default)('div').withConfig({
  displayName: "WaffleGrid__Center",
  componentId: "components-web__sc-ec8g0a-2"
})({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center'
});

/**
 * The WaffleGrid is used to show items in a waffle like manner with borders surrounding the element
 */
const WaffleGrid = /*#__PURE__*/_react.default.forwardRef((_ref2, ref) => {
  let {
    items,
    rowSize = null,
    LinkRouter,
    tokens,
    variant,
    linkRouterProps,
    ...rest
  } = _ref2;
  const viewport = (0, _componentsBase.useViewport)();
  const themeTokens = (0, _componentsBase.useThemeTokens)('WaffleGrid', tokens, variant, {
    viewport
  });
  const currentRowSize = (0, _componentsBase.useResponsiveProp)(rowSize);
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(Container, {
    ref: ref,
    ...selectProps(rest),
    children: items.map(child => /*#__PURE__*/(0, _react.createElement)(Item, {
      ...themeTokens,
      key: child.href,
      rowSize: rowSize ? currentRowSize : themeTokens.rowSize
    }, /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Link, {
      href: child.href,
      LinkRouter: child.LinkRouter || LinkRouter,
      linkRouterProps: {
        ...linkRouterProps,
        ...child.linkRouterProps
      },
      children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(Center, {
        children: [typeof child.image === 'string' ?
        /*#__PURE__*/
        // Assuming that string passed is the image URL
        (0, _jsxRuntime.jsx)(_Image.default, {
          src: child.image,
          alt: child.imageAltText,
          width: 96
        }) :
        // Otherwise it must be an arbitrary content, which we just display by itself
        child.image, /*#__PURE__*/(0, _jsxRuntime.jsx)(_componentsBase.Typography, {
          variant: {
            weight: 'semibold'
          },
          children: child.text
        })]
      })
    })))
  });
});
WaffleGrid.displayName = 'WaffleGrid';
WaffleGrid.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * The image and the link to display. `items` should be an array of objects with the following keys:
   */
  items: _propTypes.default.arrayOf(_propTypes.default.shape({
    /**
     * The src attribute for the HTML img element or custom JSX content to render instead
     */
    image: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.node]),
    /**
     * The alt attribute for the HTML img element (note that this is ignored if a custom
     * JSX content is used instead of an image URL)
     */
    imageAltText: _propTypes.default.string,
    /**
     * The text displayed under the image
     */
    text: _propTypes.default.string,
    /**
     * Target URL
     */
    href: _propTypes.default.string,
    ..._componentsBase.withLinkRouter.propTypes
  })).isRequired,
  /**
   * Sets the tokens for WaffleGrid element.
   */
  tokens: (0, _componentsBase.getTokensPropType)('WaffleGrid'),
  /**
   * Row size, optionally depending on the viewport
   */
  rowSize: _componentsBase.responsiveProps.getTypeOptionallyByViewport(_propTypes.default.number),
  ..._componentsBase.withLinkRouter.propTypes
};
var _default = exports.default = WaffleGrid;